import { isAmexDepositBlockedForMercadoPago } from '@features/booking/utils/amexChange';
import { useState, useRef, useCallback, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { BookingBreadcrumbs } from '@features/booking/components/BookingBreadcrumbs';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useCommitOrAdvance } from '@features/booking/hooks/useCommitOrAdvance';
import { ThreeDSChallengeOverlay } from '@features/booking/components/ThreeDSChallengeOverlay';
import { commitCtaKey } from '@features/booking/utils/commitMomentCopy';
import { verifyAttrs } from '@/shared/verify/core/contract';
import { capturePaymentError } from '@core/sentry';
import { useMercadoPagoSDK } from '@features/booking/hooks/useMercadoPagoSDK';
import { useStripeV2 } from '@features/booking/hooks/useStripeV2';
import { isBlockedBrazilDebit } from '@features/booking/utils/brazilDebit';
import { usePaymentFee } from '@features/booking/hooks/usePaymentFee';
import { encryptCardData } from '@features/booking/utils/encryptCardData';
import { env } from '@core/config/env';
import { generateRandomDocumentId } from '@shared/utils/randomDocument';
import { sanitizeHtml } from '@shared/utils/sanitize';
import { Private } from '@shared/components/common';
import { FeeBreakdownDisplay } from './FeeBreakdownDisplay';
import { useForeignCardUsd } from '@features/booking/hooks/useForeignCardUsd';
import { formatAmount } from '@shared/utils/formatAmount';
import { PaymentAcceptances } from './PaymentAcceptances';
import { BillingSection } from './BillingSection';

function formatCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length >= 3) {
    return digits.slice(0, 2) + '/' + digits.slice(2, 4);
  }
  return digits;
}

export function MercadoPagoPaymentForm() {
  const { t, i18n } = useTranslation('booking');
  const { t: tErrors } = useTranslation('errors');
  const booking = useBooking();
  const restaurant = useRestaurant();
  const mpSDK = useMercadoPagoSDK();
  const stripeV2 = useStripeV2(env.stripeFallbackKey);
  const { feeBreakdown, displayCurrency, slotType, cancellationHours } = usePaymentFee();
  const { usdTotal, usdBill, foreignViewer, pending: usdPending } = useForeignCardUsd(feeBreakdown);
  // A visitor from abroad sees only the USD a foreign card is charged; a local sees the local amount with a USD note.
  const showUsdOnly = foreignViewer && usdTotal != null;
  const shownCurrency = showUsdOnly ? 'USD' : displayCurrency;
  const shownAmount = showUsdOnly ? usdTotal : feeBreakdown.total;

  // T14b: Guarantee-as-commit-host wiring. See StripePaymentForm for the
  // full rationale — same pattern. The hook decides commit vs goToNext
  // based on `getNextStep()?.id === 'BOOKING_FINAL'`, computed from the
  // step set that's now flag-aware (CONFIRMATION conditional in steps.ts).
  const {
    commitOrAdvance,
    willCommit,
    nextStepPath,
    isSubmitting,
    error: commitError,
    requireReload,
    isHandling3ds,
  } = useCommitOrAdvance();

  // Ref so handleSubmit can call the LATEST commitOrAdvance after the
  // flushSync that publishes the freshly-tokenized card to bookingState.
  const commitOrAdvanceRef = useRef(commitOrAdvance);
  commitOrAdvanceRef.current = commitOrAdvance;

  const stripeFormRef = useRef<HTMLFormElement>(null);

  // Carried-over decline notice, latched ONCE at mount. A one-shot local
  // state (not a derived read of booking.paymentErrorMessage) on purpose:
  // the notice sits at the TOP of the form, so if visibility were derived
  // with the `!error && !commitError` suppression alone, every local-error
  // toggle would unmount/remount it and shift the form under the guest's
  // cursor (e.g. typing the holder name clears `error`, which would bring
  // the stale notice back mid-keystroke). Retired at the next submit —
  // after the guest acts, the notice would describe a previous attempt.
  const [declineNotice, setDeclineNotice] = useState(booking.paymentErrorMessage);

  // Focused on arrival so it is (a) scrolled into view — ScrollToTop pins
  // the redirect at the top of the page — and (b) actually announced:
  // `role="alert"` only fires on insertion/content change, and this block
  // is present in the freshly mounted page's FIRST render, so most screen
  // readers would stay silent without an explicit focus.
  const declineNoticeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (declineNotice) declineNoticeRef.current?.focus();
  }, [declineNotice]);

  // Pre-filled from a previous attempt (non-sensitive) so a guest coming
  // back after a decline only re-enters number/expiry/CVC.
  const [holderName, setHolderName] = useState(booking.cardHolderName ?? '');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');

  const [allAccepted, setAllAccepted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const feeAmount = feeBreakdown.total;
  // Only gate on MercadoPago readiness. Stripe v2 is best-effort for dual-tokenization:
  // if it fails, the backend accepts cardTokenStripe='' and handles service fees without it.
  // Gating on both would block the entire payment if Stripe CDN is slow/down.
  const sdksReady = mpSDK.isReady;

  // Random doc number (matches legacy behavior)
  const docNumberRef = useRef(
    generateRandomDocumentId().toString()
  );

  // Detect payment method once per BIN (first 6 digits). Depending on the
  // derived `bin` — not the full cardNumber — keeps the effect from
  // re-firing the lookup on every keystroke past digit 6.
  const bin = cardNumber.replace(/\D/g, '').slice(0, 6);
  useEffect(() => {
    if (bin.length >= 6 && mpSDK.isReady) {
      mpSDK.getPaymentMethod(bin)
        .then(setPaymentMethodId)
        .catch(() => { /* non-critical */ });
    }
  }, [bin, mpSDK.isReady, mpSDK.getPaymentMethod]);

  // MP tokenizes from React state directly (SDK v2 Core Methods, issue
  // #354); only Stripe v2's legacy form-reading API still needs a hidden
  // form synced before tokenization.
  const syncStripeHiddenForm = useCallback(() => {
    const digits = cardNumber.replace(/\D/g, '');
    const parts = expiry.split('/');
    const month = parts[0] || '';
    const year = parts[1] || '';

    if (stripeFormRef.current) {
      const setVal = (id: string, val: string) => {
        const el = stripeFormRef.current!.querySelector(`#sv2-${id}`) as HTMLInputElement;
        if (el) el.value = val;
      };
      setVal('name', holderName);
      setVal('number', digits);
      setVal('exp-month', month);
      setVal('exp-year', year);
      setVal('cvc', cvc);
    }
  }, [holderName, cardNumber, expiry, cvc]);

  const handleSubmit = async () => {
    if (!sdksReady || !allAccepted) return;

    // Capture commit intent at click time, BEFORE the flushSync below
    // mutates booking.cardToken. Without this snapshot the post-flushSync
    // re-render filters GUARANTEE out of `activeSteps` (its `isRequired`
    // depends on `!hasCardToken`), `getNextStep()` returns null, and the
    // dispatch silently no-ops. Same bug as Stripe form — see
    // useCommitOrAdvance interface JSDoc for the full mechanism.
    const capturedIntent = { willCommit, nextStepPath };

    setIsProcessing(true);
    setError(null);
    // The guest has acted — the carried-over decline notice now describes a
    // previous attempt. Retire it so it can't flash back mid-processing.
    setDeclineNotice(null);

    // Validate inputs
    const digits = cardNumber.replace(/\D/g, '');
    if (!holderName.trim()) {
      setError(t('FORM_ERROR_CREDIT_CARD_NAME'));
      setIsProcessing(false);
      return;
    }
    if (digits.length < 13 || digits.length > 19) {
      setError(t('FORM_ERROR_CREDIT_CARD'));
      setIsProcessing(false);
      return;
    }
    const expiryRegex = /^(0[1-9]|1[0-2])\/(\d{2})$/;
    if (!expiryRegex.test(expiry)) {
      setError(t('FORM_ERROR_CREDIT_CARD_EXPIRES'));
      setIsProcessing(false);
      return;
    }
    if (cvc.length < 3 || cvc.length > 4) {
      setError(t('FORM_ERROR_SECURITY_CODE'));
      setIsProcessing(false);
      return;
    }

    // feeBreakdown applies the menu→slot guarantee hierarchy; slotType does
    // not. The GUARANTEE step requires a payment-bearing guarantee, so a
    // null type here is an invariant violation, not a fallback case.
    const resolvedGuaranteeType = feeBreakdown.guaranteeType;
    if (resolvedGuaranteeType !== 'deposit' && resolvedGuaranteeType !== 'no_show') {
      Sentry.captureException(
        new Error('Payment submit reached without resolved guarantee type'),
        {
          extra: {
            slotType,
            feeBreakdownGuaranteeType: feeBreakdown.guaranteeType,
            feeBreakdownTotal: feeBreakdown.total,
            restaurantId: restaurant.id,
            paymentProcessor: 'mercadopago',
          },
        },
      );
      setError(t('TRADITIONAL_GRAB_ERROR'));
      setIsProcessing(false);
      return;
    }

    // Check AMEX restriction
    const firstTwo = digits.slice(0, 2);
    const isAmex = firstTwo === '34' || firstTwo === '37';
    if (isAmexDepositBlockedForMercadoPago({ isAmex, allowAMEXBR: restaurant.allowAMEXBR, guaranteeType: resolvedGuaranteeType })) {
      setError(t('STRIPE_BRAZIL_AMEX_ERROR'));
      setIsProcessing(false);
      return;
    }

    // Sync the Stripe hidden form before tokenization
    syncStripeHiddenForm();

    try {
      // Step 1: Create Stripe v2 token (best-effort)
      let cardTokenStripe = '';
      try {
        const stripeResponse = await stripeV2.createToken(stripeFormRef.current!);
        cardTokenStripe = stripeResponse.id || '';

        // Check debit card restriction (Brazil only) — uses Stripe response when available
        if (isBlockedBrazilDebit(stripeResponse.card, restaurant.defaultLanguage)) {
          setError(t('STRIPE_BRAZIL_DEBIT_CARD_ERROR'));
          setIsProcessing(false);
          return;
        }
      } catch {
        // Stripe v2 token is best-effort for MercadoPago flow
      }

      // Step 2: Create MercadoPago token (primary)
      const parts = expiry.split('/');
      const mpToken = await mpSDK.createToken({
        holderName,
        cardNumber: digits,
        securityCode: cvc,
        expirationMonth: parts[0],
        expirationYear: parts[1],
        docNumber: docNumberRef.current,
      });

      // Step 3: Encrypt card data
      const encrypted = encryptCardData({
        number: digits,
        expiryMonth: parts[0],
        expiryYear: parts[1],
        cvc,
        holderName,
      });

      // Step 4: Save everything to booking state.
      // flushSync so the next render publishes cardToken + encrypted fields
      // before commitOrAdvance reads booking. When this form is the commit
      // host, the commit would otherwise POST without the just-minted MP
      // token. Same pattern as StripePaymentForm and PersonalizePage.
      flushSync(() => {
        booking.updateBooking({
          cardToken: mpToken,
          cardTokenStripe,
          paymentMethod: paymentMethodId || null,
          paymentProcessor: 'mercadopago',
          feeDeposit: feeAmount,
          feeGratuity: feeBreakdown.gratuity,
          feeGratuityPercent: feeBreakdown.gratuityPercent,
          guaranteeType: resolvedGuaranteeType,
          encryptedCardNumber: encrypted.temp1,
          encryptedExpiry: encrypted.temp3,
          encryptedCvc: encrypted.temp4,
          encryptedHolderName: encrypted.temp6,
          // Remember the (non-sensitive) holder name for a potential retry,
          // and retire the previous attempt's decline notice — this submit
          // supersedes it.
          cardHolderName: holderName,
          paymentErrorMessage: null,
        });
      });

      // Post-flushSync: pass the click-time `willCommit` snapshot so
      // dispatch doesn't get fooled by the GUARANTEE-is-now-filtered
      // re-render (see capture comment at top of handleSubmit).
      await commitOrAdvanceRef.current(capturedIntent);
    } catch (err) {
      // User-visible: keep the translated fallback — surfacing raw
      // `err.message` would leak English SDK strings or stack-y dev
      // text into a Spanish UI. Diagnostic value belongs in Sentry +
      // DEV console, not the inline error block. (Stripe's path goes
      // through `getStripeErrorMessage` for the same reason.)
      setError(t('TRADITIONAL_GRAB_ERROR'));
      // Route the exception through the canonical helper so it lands
      // on the same Sentry dashboards as every other payment-commit
      // error: dotted `payment.processor` tag, ApiError-aware
      // fingerprinting, `isExpectedClientTransportError` filter for
      // dropped-network noise. Replacing a previous inline
      // `Sentry.captureException` that bypassed all of that.
      capturePaymentError(err, 'mercadopago', {
        restaurantId: restaurant.id,
        paymentMethodId,
        stage: 'tokenization-or-commit',
      });
      // DEV-only console.error so the live diagnostic appears in the dev
      // tools immediately. Stripped from prod builds by Vite tree-shake.
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error('[MercadoPagoPaymentForm] handleSubmit failed:', err);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <BookingPageLayout
      title={t('GRAB_CONFIRMATION_TITLE_4')}
      subtitle={t('GRAB_CONFIRMATION_TITLE_7') || undefined}
      subtitleClassName="text-foreground/70 text-lg mb-6 text-center"
      breadcrumbs={<BookingBreadcrumbs />}
    >
      <div
        className="w-full sm:px-6 space-y-6"
        {...verifyAttrs("MercadoPagoPaymentForm", {
          willCommit,
          hostStep: "guarantee",
          // T30 D1: MP renders only for AR/MX tenants on deposit/no_show
          // guarantees with non-zero fees. hasFee is effectively always
          // true in production, but the helper flips to GUARANTEE_NO_FEE
          // defensively if the BE ever waives.
          hasFee: feeBreakdown.total > 0,
          // See StripePaymentForm for the rationale — the CTA semantic
          // ("are we charging now?") splits on guaranteeType, not on
          // fee presence. no_show with fee>0 is a hold, not a charge
          // (commit 343e594).
          guaranteeType: feeBreakdown.guaranteeType ?? "",
          hasError: !!commitError,
          isSubmitting,
          isProcessing,
          requireReload,
          // T30 D2: surfaced for the verify-matrix invariant that gates the
          // ThreeDSChallengeOverlay Portal — overlay must mount iff true.
          isHandling3ds,
          ctaKey: requireReload
            ? "PAYMENT_3DS_RELOAD_TO_RETRY"
            : isProcessing
              ? "PAYMENT_PROCESSING"
              : commitCtaKey({
                  hostStep: "guarantee",
                  // hasFee=true ONLY for deposit (immediate charge).
                  // no_show is a hold — "You won't be charged at this
                  // moment" — so the CTA should not promise payment.
                  // See StripePaymentForm for the matching rationale.
                  hasFee: feeBreakdown.guaranteeType === 'deposit' && feeBreakdown.total > 0,
                  isSubmitting,
                  hasError: !!commitError,
                  isWaitingList: booking.flowType === 'waiting_list',
                }),
        })}
      >
        {booking.flowType === 'waiting_list' ? (
          <div className="text-foreground/80 text-sm space-y-2">
            <p>{t('ADD_CREDIT_CARD_TO_JOIN_WAITING_LIST_1')}</p>
            <p>{t('ADD_CREDIT_CARD_TO_JOIN_WAITING_LIST_2')}</p>
            <p>{t('NO_SHOW_GUARANTEE_APPLICABLE_1')}</p>
            <p>{t('NO_SHOW_GUARANTEE_APPLICABLE_2')}</p>
          </div>
        ) : (
          <div className="text-center text-foreground/70 text-sm">
            <p dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('NEW_MODAL_PROVIDE_CC_DETAILS')) }} />
            <p>{t('STRING_PAYMENT_CREDITED')}</p>
          </div>
        )}

        {/* Decline notice carried over from a failed commit on ANOTHER page
            (useReservationCommit cleared the consumed token and routed the
            guest back here) — tells them WHY they're re-entering the card.
            Rendered at the TOP: ScrollToTop pins the redirect at the top of
            the page, so the bottom error slot would sit below the fold.
            One-shot local latch (see declineNotice) so it never remounts
            and shifts the form after the guest acts; focused on arrival
            (see declineNoticeRef) for scroll-into-view + SR announcement.
            Gated on the latch ALONE (mirrors StripePaymentForm, where an
            `!error` guard would flicker with CardElement's mid-typing
            validation): every setError in THIS form runs inside
            handleSubmit, after the latch is retired, so an error guard
            here is provably inert — kept off for cross-form symmetry. */}
        {declineNotice && (
          <div
            ref={declineNoticeRef}
            tabIndex={-1}
            role="alert"
            data-testid="payment-decline-error"
            className="flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive outline-none"
          >
            <AlertCircle size={18} className="shrink-0" />
            <span>{declineNotice}</span>
          </div>
        )}

        {/* SDK loading state */}
        {!sdksReady && (
          <div className="text-center text-foreground/50 text-sm py-4">
            {mpSDK.error ? t('PAYMENT_SDK_LOAD_ERROR') : t('PAYMENT_LOADING')}
          </div>
        )}

        {/* Card input fields (visible) — wrapped in <Private> so session-
            replay tools (FullStory) never capture keystrokes. PCI-DSS:
            MercadoPago's flow keeps PAN / CVC / expiry in React state and
            plain inputs (NOT iframe-isolated like Stripe Elements), so
            without this annotation a recorder would store card data in a
            third-party SaaS. `data-private` is inert to React, MP SDK, and
            Stripe v1 SDK — see Private.tsx for the contract. */}
        {sdksReady && (
          <Private className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">
                {t('PAYMENT_LABEL_CC_2')}
              </label>
              <input
                data-testid="card-holder"
                type="text"
                className="w-full border border-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                value={holderName}
                onChange={(e) => {
                  setHolderName(e.target.value);
                  // Clear the "Cardholder name is required" submit error the
                  // moment the user starts typing (same pattern as Stripe
                  // form). MP form has several other field validations that
                  // set the same `error` state, so we only clear when this
                  // particular field transitions to non-empty.
                  if (e.target.value.trim() && error) setError(null);
                }}
                placeholder="John Doe"
              />
            </div>

            <BillingSection />

            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">
                {t('PAYMENT_LABEL_CC')}
              </label>
              <input
                data-testid="card-number"
                type="text"
                className="w-full border border-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                placeholder="4242 4242 4242 4242"
                maxLength={23}
                inputMode="numeric"
              />
            </div>

            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground/70 mb-1">
                  {t('STRING_PAYMENT_EXPIRES')}
                </label>
                <input
                  data-testid="card-expiry"
                  type="text"
                  className="w-full border border-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                  value={expiry}
                  onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                  placeholder="MM/YY"
                  maxLength={5}
                  inputMode="numeric"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground/70 mb-1">
                  {t('STRING_SECURITY_CODE')}
                </label>
                <input
                  data-testid="card-cvc"
                  type="text"
                  className="w-full border border-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                  value={cvc}
                  onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="123"
                  maxLength={4}
                  inputMode="numeric"
                />
              </div>
            </div>
          </Private>
        )}

        {/* Hidden Stripe v2 form — `data-private` belt-and-suspenders even
            though `display: none` elements are typically skipped by
            recorders. syncStripeHiddenForm() writes card data into these.
            (MP no longer needs a hidden form: SDK v2 tokenizes from the
            values passed to createToken directly — issue #354.) */}
        <form ref={stripeFormRef} name="stripeForm" data-private style={{ display: 'none' }}>
          <input id="sv2-name" data-stripe="name" type="hidden" />
          <input id="sv2-number" data-stripe="number" type="hidden" />
          <input id="sv2-exp-month" data-stripe="exp-month" type="hidden" />
          <input id="sv2-exp-year" data-stripe="exp-year" type="hidden" />
          <input id="sv2-cvc" data-stripe="cvc" type="hidden" />
        </form>

        <FeeBreakdownDisplay feeBreakdown={feeBreakdown} displayCurrency={displayCurrency} foreignCardUsd={showUsdOnly ? null : usdTotal} usdBill={showUsdOnly ? usdBill : null} />

        <PaymentAcceptances
          guaranteeType={feeBreakdown.guaranteeType}
          feeAmount={shownAmount}
          displayCurrency={shownCurrency}
          serviceFee={feeBreakdown.serviceFee}
          cancellationHours={cancellationHours}
          isWaitingList={booking.flowType === 'waiting_list'}
          onAllAcceptedChange={setAllAccepted}
        />

        {error && (
          <div
            role="alert"
            className="flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            <AlertCircle size={18} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Commit-host error from useReservationCommit (post-tokenization
            failures, 3DS rejects, backend bookings blocks). Null when
            this form isn't the commit host. */}
        {commitError && (
          <div
            role="alert"
            data-testid="payment-commit-error"
            className="flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            <AlertCircle size={18} className="shrink-0" />
            <span>{commitError}</span>
          </div>
        )}

      </div>

      <div className="w-full sm:px-6 mt-8">
        <Button
          data-testid="payment-submit"
          {...verifyAttrs("MercadoPagoPaymentSubmit", { action: "confirm-booking" })}
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md disabled:opacity-50"
          onClick={handleSubmit}
          disabled={!sdksReady || !allAccepted || isProcessing || isSubmitting || requireReload || usdPending}
        >
          {requireReload
            ? tErrors('PAYMENT_3DS_RELOAD_TO_RETRY')
            : isProcessing
              ? // Tokenization in flight (Stripe v2 fallback + MP token).
                t('PAYMENT_PROCESSING')
              : // T30 D1: copy reflects what the user authorizes regardless
                // of whether the click commits or advances. See the matching
                // comment in StripePaymentForm.
                t(commitCtaKey({
                  hostStep: 'guarantee',
                  // See verifyAttrs comment above. Deposit-only "pay
                  // now" semantics; no_show is a hold and uses the
                  // simpler "Reservar" copy.
                  hasFee: feeBreakdown.guaranteeType === 'deposit' && feeBreakdown.total > 0,
                  isSubmitting,
                  hasError: !!commitError,
                  isWaitingList: booking.flowType === 'waiting_list',
                }), {
                  amount: `${shownCurrency} ${formatAmount(shownAmount, i18n.language)}`,
                })}
        </Button>
      </div>

      {/* T30 D2: 3DS overlay. Inert unless useReservationCommit's commit
          path triggers a 3DS challenge mid-flight. */}
      <ThreeDSChallengeOverlay open={isHandling3ds} />
    </BookingPageLayout>
  );
}
